import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import api from '../../services/api';

const EditarCurso = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [curso, setCurso] = useState({
    titulo: '',
    descricao: '',
    nivel: 'Iniciante',
    categoria_id: '',
    instrutor_id: '',
    capa: null
  });
  
  const [categorias, setCategorias] = useState([]);
  const [instrutores, setInstrutores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [previewImage, setPreviewImage] = useState(null);

  // Fetch inicial dos dados
  useEffect(() => {
    fetchDados();
  }, [id]);

  const fetchDados = async () => {
    try {
      setLoading(true);
      
      // Buscar dados em paralelo
      const [cursoResponse, categoriasResponse, instrutoresResponse] = await Promise.all([
        api.get(`/cursos/${id}`),
        api.get('/categorias'),
        api.get('/instrutores')
      ]);

      // Tratar dados do curso
      setCurso(cursoResponse.data || {
        titulo: '',
        descricao: '',
        nivel: 'Iniciante',
        categoria_id: '',
        instrutor_id: '',
        capa: null
      });
      
      // Tratar dados das categorias - formato: {categorias: [...]}
      const categoriasData = categoriasResponse.data?.categorias || [];
      setCategorias(Array.isArray(categoriasData) ? categoriasData : []);
      
      // Tratar dados dos instrutores - formato: {success: true, instrutores: [...]}
      const instrutoresData = instrutoresResponse.data?.instrutores || [];
      setInstrutores(Array.isArray(instrutoresData) ? instrutoresData : []);
      
      // Set preview da imagem atual se existir
      if (cursoResponse.data?.capa) {
        setPreviewImage(`${api.defaults.baseURL.replace('/api', '')}/uploads/${cursoResponse.data.capa}`);
      }
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      
      let errorMessage = 'Erro ao carregar dados do curso.';
      if (error.response?.status === 401) {
        errorMessage = 'Acesso negado. Faça login como administrador.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Curso não encontrado.';
      } else if (error.response?.data?.erro) {
        errorMessage = error.response.data.erro;
      }
      
      setMessage({ 
        type: 'error', 
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurso(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCurso(prev => ({
        ...prev,
        capa: file
      }));
      
      // Criar preview da nova imagem
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!curso.titulo || !curso.titulo.trim()) {
      setMessage({ type: 'error', text: 'O título é obrigatório' });
      return;
    }

    try {
      setSaving(true);
      
      const formData = new FormData();
      formData.append('titulo', curso.titulo || '');
      formData.append('descricao', curso.descricao || '');
      formData.append('nivel', curso.nivel || 'Iniciante');
      formData.append('categoria_id', curso.categoria_id || '');
      formData.append('instrutor_id', curso.instrutor_id || '');
      
      if (curso.capa && curso.capa instanceof File) {
        formData.append('capa', curso.capa);
      }

      await api.put(`/cursos/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setMessage({ type: 'success', text: 'Curso atualizado com sucesso!' });
      
      // Redirecionar após 2 segundos
      setTimeout(() => {
        navigate('/admin/cursos');
      }, 2000);
      
    } catch (error) {
      console.error('Erro ao atualizar curso:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Erro ao atualizar curso. Tente novamente.' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/cursos');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Editar Curso</h1>
          <p className="text-slate-300 mt-2">Atualize as informações do curso</p>
        </div>
        <Button 
          onClick={handleCancel}
          variant="outline"
          className="border-slate-600 text-slate-300 hover:bg-slate-700/50"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Voltar
        </Button>
      </div>

      {/* Mensagem de Feedback */}
      {message.text && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success' 
            ? 'bg-green-900/20 border-green-500/30 text-green-400' 
            : 'bg-red-900/20 border-red-500/30 text-red-400'
        }`}>
          <div className="flex items-center">
            {message.type === 'success' ? (
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            {message.text}
          </div>
        </div>
      )}

      {/* Formulário */}
      <Card className="p-6 bg-slate-800/50 border-slate-700/50 backdrop-blur-sm">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Título */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Título do Curso *
            </label>
                          <input
                type="text"
                name="titulo"
                value={curso.titulo || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Digite o título do curso"
                required
              />
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Descrição
            </label>
                          <textarea
                name="descricao"
                value={curso.descricao || ''}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                placeholder="Descreva o conteúdo do curso"
              />
          </div>

          {/* Grid para Nível, Categoria e Instrutor */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Nível */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Nível
              </label>
              <select
                name="nivel"
                value={curso.nivel || 'Iniciante'}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="Iniciante">Iniciante</option>
                <option value="Intermediário">Intermediário</option>
                <option value="Avançado">Avançado</option>
              </select>
            </div>

            {/* Categoria */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Categoria
              </label>
              <select
                name="categoria_id"
                value={curso.categoria_id || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Selecione uma categoria</option>
                {categorias && categorias.length > 0 && categorias.map(categoria => (
                  <option key={categoria.id} value={categoria.id}>
                    {categoria.nome}
                  </option>
                ))}
              </select>
            </div>

            {/* Instrutor */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Instrutor
              </label>
              <select
                name="instrutor_id"
                value={curso.instrutor_id || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Selecione um instrutor</option>
                {instrutores && instrutores.length > 0 && instrutores.map(instrutor => (
                  <option key={instrutor.id} value={instrutor.id}>
                    {instrutor.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Capa do Curso */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Capa do Curso
            </label>
            
            {/* Preview da imagem */}
            {previewImage && (
              <div className="mb-4">
                <div className="relative inline-block">
                  <img
                    src={previewImage}
                    alt="Preview da capa"
                    className="w-48 h-32 object-cover rounded-lg border border-slate-600/50"
                  />
                  <div className="absolute inset-0 bg-black/20 rounded-lg flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <span className="text-white text-sm">Imagem atual</span>
                  </div>
                </div>
              </div>
            )}
            
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white file:mr-4 file:py-1 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-sm text-slate-400 mt-1">
              Deixe em branco para manter a imagem atual. Formatos aceitos: JPG, PNG, GIF
            </p>
          </div>

          {/* Botões de Ação */}
          <div className="flex gap-4 pt-6">
            <Button
              type="submit"
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Salvando...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Salvar Alterações
                </>
              )}
            </Button>
            
            <Button
              type="button"
              onClick={handleCancel}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700/50"
              disabled={saving}
            >
              Cancelar
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default EditarCurso; 